import { Module, VuexModule, Mutation, Action, getModule } from 'vuex-module-decorators'
import { store } from '../../store'
import { DecodedToken } from '../../models/decoded-token'
import { jwtDecode } from 'jwt-decode'
import { userStore } from '../../store/modules/user.store'
import { GoogleAuth } from '../../services/google-auth'
import { FacebookAuth } from '../../services/facebook-auth'
import { appConfig } from '../../services/app-config'
import { LocalStorage } from '../../services/local-storage'
import { authApi } from '../../services/api/auth.api'
import { appSettingsStore } from './app-settings.store'
import { dataSubscriptionStore } from './data-subscriptions.store'
import emitter from '../../services/emitter'
import { httpClient } from '../../services/http-client'

@Module({
    dynamic: true,
    namespaced: true,
    name: 'auth',
    store,
})
class AuthStore extends VuexModule {
    private token: string | null = null
    private googleAuth: GoogleAuth | null = null
    private facebookAuth: FacebookAuth | null = null
    private accessTokenRefreshTimeout

    get isLoggedIn(): boolean {
        return this.token != null
    }

    get encodedToken() {
        return this.token
    }

    get decodedToken() {
        try {
            return jwtDecode(this.token!) as DecodedToken
        } catch {
            return null
        }
    }

    get GAuth() {
        return this.googleAuth
    }

    @Action({ rawError: true })
    async init() {
        const token = LocalStorage.getToken()
        if (token) {
            this.setToken({ token: token.value, rememberMe: token.rememberMe })
            await this.setTimer()
        }
    }

    @Action({ rawError: true })
    async getGAuth() {
        // initialized on demand
        if (this.googleAuth) {
            return Promise.resolve(this.googleAuth)
        } else {
            const googleAuth = new GoogleAuth()
            await googleAuth.load(appConfig.googleOAuthClientId!)
            this.setGoogleAuth(googleAuth)
            return googleAuth
        }
    }

    @Mutation
    setGoogleAuth(googleAuth: GoogleAuth) {
        this.googleAuth = googleAuth
    }

    @Mutation
    setFacebookAuth(facebookAuth: FacebookAuth) {
        this.facebookAuth = facebookAuth
    }

    @Mutation
    setToken(data: { token: string | null, rememberMe: boolean }) {
        this.token = data.token!
        httpClient.defaults.headers.common = { Authorization: 'Bearer ' + this.token }
        LocalStorage.setToken(this.token, data.rememberMe)
    }

    @Action({ rawError: true })
    async setTimer() {
        const jwtToken = jwtDecode(this.token!) as DecodedToken

        // Clear previous timer
        clearTimeout(this.accessTokenRefreshTimeout)

        // Set a timeout to refresh the access token a minute before it expires
        const expires = new Date(jwtToken.exp * 1000)
        const minutes = (60 * 1000)
        const timeout = expires.getTime() - Date.now() - minutes

        if (timeout < minutes) {
            console.log('Access token expires soon or is expired, refreshing')
            await this.refreshAccessToken()
        } else {
            this.accessTokenRefreshTimeout = setTimeout(async () => {
                console.log('Access token expires is a minute, refreshing')
                await this.refreshAccessToken()
            }, timeout)
        }
    }

    @Action({ rawError: true })
    async refreshAccessToken() {
        const token = await authApi.refreshAccessToken()
        if (token !== '') {
            this.setToken({ token, rememberMe: LocalStorage.isTokenRemembered() })
            await this.setTimer()
        }
    }

    @Action({ rawError: true })
    async login(data: { token: string, rememberMe: boolean }) {
        this.setToken(data)
        await this.setTimer()
        await this.initStores()
        emitter.emit('loggedIn')
    }

    @Action({ rawError: true })
    async initStores() {
        await Promise.all([
            appSettingsStore.init(),
            dataSubscriptionStore.init(),
            userStore.init()
        ])
    }

    @Mutation
    logout() {
        userStore.setUser(null)
        dataSubscriptionStore.setCurrentSubscriptions(null)
        LocalStorage.removeToken()
        authApi.revokeRefreshToken()
        this.token = null
        clearTimeout(this.accessTokenRefreshTimeout)

        // Matomo
        // User has just logged out, we reset the User ID
        window['_paq'].push(['resetUserId'])
        // we also force a new visit to be created for the pageviews after logout
        window['_paq'].push(['appendToTrackingUrl', 'new_visit=1'])
        window['_paq'].push(['trackPageView'])
        // we finally make sure to not again create a new visit afterwards (important for Single Page Applications)
        window['_paq'].push(['appendToTrackingUrl', ''])
        
        emitter.emit('loggedOut')
    }
}

export const authStore = getModule(AuthStore)
